import React, { useState, useCallback, JSX } from 'react';
import EDChip from '../EDChip';
import EDTooltip from '../../Tooltip/EDTooltip';
import { AvatarProps } from '@mui/material/Avatar';
import { IconType } from '../../Icon/Utils/IconDefinitions';
import EDAvatar from '../../Avatar/EDAvatar';
import EDIcon, { IconColor } from '../../Icon/EDIcon';
import EDBox from '../../EDBox/EDBox';
import { useIsTouchDevice } from '../../../Components/BreakpointContext/BreakpointContextUtils';
interface IProps {
  isDragging?: boolean;
  name: string;
  showTooltip: boolean;
  onDelete?: () => void;
  onClick?: React.MouseEventHandler<any>;
  chipSelection?: boolean;
  onAvatarClick?: AvatarProps['onClick'];
  dragHandle?: boolean;
  avatarHighLighted?: boolean;
  avatarTooltip?: string;
  alternateAvatar?: IconType;
  clickDisabled?: boolean;
  labelIcon?: React.ReactNode;
  disableRipple?: boolean;
  showHidden?: boolean;
  hidden?: boolean;
  deleteIcon?: JSX.Element;
  hoverAvatar?: JSX.Element;
}

/**
 * Utseendet för ett DraggableChip
 */
const EDDraggableChipView: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const isTouchDevice = useIsTouchDevice();
  const [isHovering, setIsHovering] = useState(false);
  const enterHover = useCallback(() => {
    setIsHovering(true);
  }, []);
  const exitHover = useCallback(() => {
    setIsHovering(false);
  }, []);
  const toggleHover = useCallback(() => {
    setIsHovering(!isHovering);
  }, [isHovering]);
  const opacity = props.isDragging ? 0 : 1;
  const iconColor: IconColor | undefined =
    props.showHidden && props.onDelete
      ? props.hidden
        ? 'primary'
        : 'white'
      : undefined;
  const color = props.showHidden && props.onDelete ? 'primary' : 'default';
  const showOutLined = props.showHidden && props.hidden;
  const disabledInteraction = !props.onDelete;
  const variant = showOutLined ? 'outlined' : undefined;
  const usedAvatarComponent =
    props.hoverAvatar && isHovering ? (
      props.hoverAvatar
    ) : (
      <EDAvatar
        onClick={props.onAvatarClick}
        sx={{
          boxSizing: 'border-box',
          cursor: props.dragHandle ? 'grab' : undefined,
          '&:hover': {
            cursor: props.chipSelection ? 'pointer' : undefined,
          },
          background:
            props.chipSelection && props.avatarHighLighted
              ? theme => theme.palette.primary.light
              : showOutLined && !disabledInteraction
              ? theme => theme.palette.common.white + ' !important'
              : undefined,
          color:
            showOutLined && !disabledInteraction
              ? theme => theme.palette.primary.light + ' !important'
              : undefined,
          border: theme =>
            showOutLined && !disabledInteraction
              ? `1px solid ${theme.palette.primary.dark}`
              : showOutLined && disabledInteraction
              ? `1px solid ${theme.palette.grey[500]}`
              : undefined,
          marginLeft:
            showOutLined && !disabledInteraction
              ? '-1px !important'
              : showOutLined && disabledInteraction
              ? '-1px !important'
              : undefined,
          backgroundColor:
            showOutLined && disabledInteraction ? 'unset' : undefined,
        }}
      >
        <EDTooltip title={props.avatarTooltip ? props.avatarTooltip : ''}>
          <EDBox
            sx={{
              height: theme => theme.spacing(3),
              width: theme => theme.spacing(3),
            }}
          >
            <EDIcon
              color={iconColor}
              iconType={
                props.alternateAvatar ? props.alternateAvatar : 'drag_handle'
              }
            />
          </EDBox>
        </EDTooltip>
      </EDAvatar>
    );
  const specialHover = !props.onClick && isTouchDevice;
  return (
    <EDChip
      onMouseEnter={!specialHover ? enterHover : undefined}
      onMouseLeave={!specialHover ? exitHover : undefined}
      deleteIcon={props.deleteIcon}
      color={color}
      variant={variant}
      opacity={opacity}
      avatar={usedAvatarComponent}
      label={
        <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
          {props.labelIcon}
          <EDTooltip title={props.showTooltip ? props.name : undefined}>
            <EDBox
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 'initial',
                width: '100%',
                textAlign: 'right',
              }}
            >
              {props.name}
            </EDBox>
          </EDTooltip>
        </EDBox>
      }
      fullWidth
      onDelete={props.onDelete}
      grabbable
      dragHandle={props.dragHandle && !props.clickDisabled}
      onClick={specialHover ? toggleHover : props.onClick}
    />
  );
};
export default EDDraggableChipView;
